import { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import type {
  Ticket,
  CreateTicketRequest,
  CreateTicketResponse,
  QueryTicketsRequest,
  QueryTicketsResponse,
} from '../types/api';

interface UseTicketsOptions {
  autoRefresh?: boolean;
  refreshInterval?: number;
  filters?: QueryTicketsRequest;
}

interface UseTicketsReturn {
  tickets: Ticket[];
  total: number;
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  createTickets: (request: CreateTicketRequest) => Promise<CreateTicketResponse>;
  updateTicket: (ticketId: string, updates: Partial<Ticket>) => Promise<Ticket>;
  deleteTicket: (ticketId: string) => Promise<void>;
}

export function useTickets(options: UseTicketsOptions = {}): UseTicketsReturn {
  const { autoRefresh = false, refreshInterval = 30000, filters } = options;

  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchTickets = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data: QueryTicketsResponse = await apiService.getTickets(filters);
      setTickets(data.tickets);
      setTotal(data.total);
    } catch (err) {
      setError(err as Error);
      console.error('Failed to fetch tickets:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const refresh = useCallback(async () => {
    await fetchTickets();
  }, [fetchTickets]);

  const createTickets = useCallback(async (request: CreateTicketRequest): Promise<CreateTicketResponse> => {
    try {
      const response = await apiService.createTickets(request);
      await fetchTickets();
      return response;
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  }, [fetchTickets]);

  const updateTicket = useCallback(async (ticketId: string, updates: Partial<Ticket>): Promise<Ticket> => {
    try {
      const updatedTicket = await apiService.updateTicket(ticketId, updates);
      await fetchTickets();
      return updatedTicket;
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  }, [fetchTickets]);

  const deleteTicket = useCallback(async (ticketId: string) => {
    try {
      await apiService.deleteTicket(ticketId);
      await fetchTickets();
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  }, [fetchTickets]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  useEffect(() => {
    if (autoRefresh && refreshInterval > 0) {
      const interval = setInterval(fetchTickets, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, refreshInterval, fetchTickets]);

  return {
    tickets,
    total,
    loading,
    error,
    refresh,
    createTickets,
    updateTicket,
    deleteTicket,
  };
}
